// import type { ChatOptions, ReceivedChatMessage } from '@livekit/components-core';
// import { setupChat } from '@livekit/components-core';
// import { useRoomContext } from '../context';
// import { useObservableState } from './internal/useObservableState';
import { LocalVideoTrack } from 'livekit-client'

import { BackgroundProcessor, CanvasCaptureBackgroundPublisher } from '@/src/processor'
import { detectBackgroundPipeline } from '@/src/lib/detectBackgroundPipeline'

import { BgEffect } from '@/src/lib/types'

/**
 * The `useChat` hook provides chat functionality for a LiveKit room.
 * It returns a simple `send` function to send chat messages and an array of `chatMessages` to hold received messages.
 * It also returns a `update` function that allows you to implement message-edit functionality.
 * @remarks
 * It is possible to pass configurations for custom message encoding and decoding and non-default topics on which to send the messages.
 * @public
 */

let processor

let capturePublisher: CanvasCaptureBackgroundPublisher | undefined

let captureTrackRef: LocalVideoTrack | undefined

/** Издатель захвата активен (включая режим превью до входа). */
export function isCapturePreviewRunning() {
  return capturePublisher !== undefined
}

export async function useToggleTrackProcessor(
  track: LocalVideoTrack,
  currentBgEffect: BgEffect | null,
  previewVideo?: HTMLVideoElement | null,
) {
  if (!track) return
  if (detectBackgroundPipeline() === 'capture-stream') {
    await toggleCapturePipeline(track, currentBgEffect, previewVideo)
    return track
  }
  if (capturePublisher) {
    await capturePublisher.stop()
    capturePublisher = undefined
  }
  //@ts-ignore нет интерфейса для процессора
  if (track.processor?.name === currentBgEffect?.type) return
  const trackHandler = async () => {
    if (currentBgEffect?.type === 'none') {
      //@ts-ignore нет интерфейса для процессора
      if (track.processor?.name) {
        await track.stopProcessor()
        processor = undefined
      }
      return
    } else {
      //@ts-ignore нет интерфейса для процессора
      if (!track.processor?.name) {
        processor = BackgroundProcessor(currentBgEffect.options)
        await track.setProcessor(processor, true)
        processor.updateProcessorName(currentBgEffect.type)
      } else {
        processor.updateTransformerOptions(currentBgEffect.options)
        processor.updateProcessorName(currentBgEffect.type)
      }
    }
  }
  await trackHandler()

  return track
}

function restoreRawPreview(previewVideo: HTMLVideoElement | null | undefined, track: LocalVideoTrack) {
  if (previewVideo) {
    previewVideo.srcObject = new MediaStream([track.mediaStreamTrack])
  }
}

async function toggleCapturePipeline(
  track: LocalVideoTrack,
  currentBgEffect: BgEffect | null,
  previewVideo?: HTMLVideoElement | null,
) {
  if (capturePublisher && captureTrackRef !== track) {
    await capturePublisher.stop()
    capturePublisher = undefined
    captureTrackRef = undefined
  }
  if (!currentBgEffect || currentBgEffect.type === 'none') {
    if (capturePublisher) {
      await capturePublisher.stop()
      capturePublisher = undefined
      captureTrackRef = undefined
    }
    restoreRawPreview(previewVideo, track)
    return
  }
  if (!capturePublisher) {
    try {
      await track.unmute()
    } catch {
      // Камера недоступна — конвейер не стартует.
    }
    const publisher = new CanvasCaptureBackgroundPublisher(track, currentBgEffect.options)
    try {
      await publisher.startPreview()
    } catch (error) {
      console.debug('capture-stream pipeline failed to start', error)
      return
    }
    capturePublisher = publisher
    captureTrackRef = track
  } else {
    capturePublisher.updateOptions(currentBgEffect.options)
  }
  const previewStream = capturePublisher.getPreviewStream()
  if (previewVideo && previewStream) {
    previewVideo.srcObject = previewStream
  }
  try {
    await capturePublisher.attachToPublishedTrack()
  } catch (error) {
    // Трек ещё не опубликован (превью до входа): остаёмся в режиме превью,
    // подмена случится при следующем переключении или событии комнаты.
    console.debug('capture-stream pipeline is not available yet', error)
  }
}
